import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import ActionButton from 'components/shared/actionButton';
import { ACTION_BUTTON_TYPES } from 'components/shared/actionButton/config';
import CustomButton from 'components/shared/customButton';
import CustomFormDrawer from 'components/shared/customFormDrawer';
import CustomSwitch from 'components/shared/customSwitch';
import PageContainer from 'components/shared/pageContainer';
import StatusIndicator from 'components/shared/statusIndicator';
import SuccessionPoolInfo from 'components/shared/successionPoolInfo';
import SuccessionPoolSection from 'components/shared/successionPoolSection';
import UserAvatar from 'components/shared/userAvatar';
import {
  prepareChangelogHeaders,
  prepareChangelogRows,
} from 'components/pages/successionKeyJobPage/config';
import {
  api_succession_talent_pool_members,
  api_succession_talent_pool_member,
} from 'constants/apiRoutes';
import {
  APP_PAGES,
  PARAMS,
  SUCCESSION_POOL_MEMBERS_DEFAULT_PARAMS,
} from 'constants/pages';
import {
  SUCCESSION_POOL_MEMBER_FORM_MODE,
  SUCCESSION_POOL_STATUS,
} from 'constants/succession';
import { sticky } from 'constants/helperCssRules';
import { successionPoolSelector } from 'store/selectors/successionPoolSelector';
import { successionSettingsSelector } from 'store/selectors/successionSettingsSelector';
import { peopleSelector } from 'store/selectors/peopleSelector';
import { getAllUsers } from 'store/modules/people';
import {
  getSuccessionPool,
  getSuccessionPoolChangelog,
  getSuccessionPoolMembers,
  clearSuccessionPool,
} from 'store/modules/successionPool';
import {
  getSuccessionReadiness,
  getSuccessionRemovalReasons,
  clearSuccessionSettings,
} from 'store/modules/successionSettings';
import {
  prepareReadinessLevelOptions,
  prepareRemovalReasonOptions,
  prepareSuccessionTableActionColumn,
  isSuccessionUserRemoved,
} from 'utility/succession';
import http from '../../../utility/http';
import { hasNextPage, showSuccessMessage } from '../../../utility/uiUtils';
import { useTranslations } from '../../../utility/useTranslations';
import {
  prepareAvailableMembers,
  preparePoolMemberTableHeaders,
  prepareAddMembersPayload,
  prepareEditMemberPayload,
  preparePoolMemberFormFields,
  preparePoolMemberFormInitialData,
  prepareRemoveMemberPayload,
  preparePoolMemberTableRows,
} from './config';

const { ADD, EDIT, REMOVE } = SUCCESSION_POOL_MEMBER_FORM_MODE;
const { page: defaultPage } = SUCCESSION_POOL_MEMBERS_DEFAULT_PARAMS;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(3),
  },
  showRemovedSwitch: {
    width: 'auto',
  },
  showRemovedLabel: {
    marginRight: spacing(1.5),
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  changelogAction: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
}));

const SuccessionPoolPage = ({ location, navigate, dispatch, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SUCCESSION_POOL);
  const { poolId } = useParams();
  const { pool, members, changelog } = useSelector(successionPoolSelector);
  const settings = useSelector(successionSettingsSelector);
  const { allUsers } = useSelector(peopleSelector);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [shouldShowRemoved, setShouldShowRemoved] = useState(false);
  const [isManageDrawerOpened, setIsManageDrawerOpened] = useState(false);
  const [manageMemberDrawer, setManageMemberDrawer] = useState(null);
  const [membersPage, setMembersPage] = useState(defaultPage);
  const [changelogPage, setChangelogPage] = useState(defaultPage);

  const isArchived = pool?.status === SUCCESSION_POOL_STATUS.ARCHIVED;

  const hasNextMembersPage = useMemo(() => hasNextPage(members), [members]);
  const hasNextChangelogPage = useMemo(
    () => hasNextPage(changelog),
    [changelog]
  );

  const memberMode = manageMemberDrawer?.mode || ADD;

  const isViewingRemoved = useMemo(() => {
    return (
      memberMode === REMOVE &&
      isSuccessionUserRemoved(manageMemberDrawer?.member?.status)
    );
  }, [memberMode, manageMemberDrawer?.member]);

  const memberDrawerTitle = useMemo(() => {
    if (memberMode === REMOVE) {
      if (isViewingRemoved) {
        return translations.memberForm.viewRemoved;
      }
      return translations.memberForm.remove;
    }
    if (memberMode === EDIT) return translations.memberForm.edit;
    return translations.memberForm.add;
  }, [translations.memberForm, memberMode, isViewingRemoved]);

  const memberFormFields = useMemo(
    () => preparePoolMemberFormFields(memberMode, manageMemberDrawer?.member),
    [memberMode, manageMemberDrawer?.member]
  );

  const memberFormInitialData = useMemo(
    () =>
      preparePoolMemberFormInitialData(
        memberMode,
        manageMemberDrawer?.member || {}
      ),
    [memberMode, manageMemberDrawer?.member]
  );

  const availableMembers = useMemo(
    () => prepareAvailableMembers(allUsers, members.results),
    [allUsers, members.results]
  );

  const onLoadMoreMembers = useCallback(() => {
    const nextPage = membersPage + 1;
    setMembersPage(nextPage);
    getSuccessionPoolMembers(
      dispatch,
      poolId,
      { [PARAMS.PAGE]: nextPage, [PARAMS.SHOW_REMOVED]: shouldShowRemoved },
      true
    );
  }, [dispatch, poolId, membersPage, shouldShowRemoved]);

  const onLoadMoreChangelog = useCallback(() => {
    const nextPage = changelogPage + 1;
    setChangelogPage(nextPage);
    getSuccessionPoolChangelog(dispatch, poolId, true, nextPage);
  }, [dispatch, poolId, changelogPage]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          getSuccessionReadiness(dispatch),
          getSuccessionRemovalReasons(dispatch),
          getAllUsers(dispatch),
          getSuccessionPool(dispatch, poolId),
          getSuccessionPoolMembers(dispatch, poolId),
          getSuccessionPoolChangelog(dispatch, poolId),
        ]);
      } finally {
        if (active) setIsInitialLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      clearSuccessionPool(dispatch);
      clearSuccessionSettings(dispatch);
    };
  }, [dispatch, poolId]);

  const handleOpenMemberDrawer = useCallback(
    (mode = ADD, member = null) =>
      () => {
        setIsManageDrawerOpened(true);
        setManageMemberDrawer({ mode, member });
      },
    []
  );

  const handleCloseMemberDrawer = useCallback(() => {
    setIsManageDrawerOpened(false);
  }, []);

  const handleSaveMember = useCallback(
    async values => {
      const mode = manageMemberDrawer?.mode;
      const memberId = manageMemberDrawer?.member?.id;

      if (mode === REMOVE) {
        await http.delete(
          api_succession_talent_pool_member(poolId, memberId),
          prepareRemoveMemberPayload(values)
        );
        showSuccessMessage(translations.successMessages.memberRemoved);
      } else if (mode === EDIT) {
        await http.patch(
          api_succession_talent_pool_member(poolId, memberId),
          prepareEditMemberPayload(values)
        );
        showSuccessMessage(translations.successMessages.memberUpdated);
      } else {
        await http.post(
          api_succession_talent_pool_members(poolId),
          prepareAddMembersPayload(values)
        );
        showSuccessMessage(translations.successMessages.membersAdded);
      }
      setMembersPage(defaultPage);
      setChangelogPage(defaultPage);
      getSuccessionPoolMembers(dispatch, poolId);
      getSuccessionPoolChangelog(dispatch, poolId);
    },
    [
      dispatch,
      poolId,
      manageMemberDrawer?.member?.id,
      manageMemberDrawer?.mode,
      translations,
    ]
  );

  const memberHeaders = useMemo(
    () => preparePoolMemberTableHeaders(translations.columns),
    [translations.columns]
  );
  const changelogHeaders = useMemo(
    () => prepareChangelogHeaders(translations.columns),
    [translations.columns]
  );
  const memberActionsColumn = useMemo(
    () =>
      isArchived
        ? []
        : prepareSuccessionTableActionColumn(translations.columns.actions),
    [translations.columns.actions, isArchived]
  );

  const readinessOptions = useMemo(
    () => prepareReadinessLevelOptions(settings?.readiness_levels),
    [settings]
  );

  const removalReasons = useMemo(
    () => prepareRemovalReasonOptions(settings?.removal_reasons),
    [settings]
  );

  const handleShowRemovedChange = useCallback(
    isChecked => {
      setShouldShowRemoved(isChecked);
      setMembersPage(defaultPage);
      getSuccessionPoolMembers(dispatch, poolId, {
        [PARAMS.SHOW_REMOVED]: isChecked,
      });
    },
    [dispatch, poolId]
  );

  const renderUser = useCallback(
    user => <UserAvatar user={user} variant="subtitle1" caption small />,
    []
  );

  const renderStatus = useCallback(
    status => <StatusIndicator status={status} />,
    []
  );

  const renderChangeLogEntryAction = useCallback(
    (user, text) => (
      <div className={classes.changelogAction}>
        {user ? (
          <UserAvatar user={user} variant="subtitle1" caption small />
        ) : null}
        <span>{text}</span>
      </div>
    ),
    [classes.changelogAction]
  );

  const renderActions = useCallback(
    currentMember => {
      if (isArchived) {
        return null;
      }

      if (isSuccessionUserRemoved(currentMember?.status)) {
        return (
          <div className={classes.rowActions}>
            <ActionButton
              type={ACTION_BUTTON_TYPES.PREVIEW}
              tooltipText={translations.menu.view}
              onClickHandler={handleOpenMemberDrawer(REMOVE, currentMember)}
            />
          </div>
        );
      }

      return (
        <div className={classes.rowActions}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.EDIT}
            tooltipText={translations.menu.edit}
            onClickHandler={handleOpenMemberDrawer(EDIT, currentMember)}
          />
          <ActionButton
            type={ACTION_BUTTON_TYPES.DELETE}
            tooltipText={translations.menu.remove}
            onClickHandler={handleOpenMemberDrawer(REMOVE, currentMember)}
          />
        </div>
      );
    },
    [classes.rowActions, isArchived, translations.menu, handleOpenMemberDrawer]
  );

  const memberRows = useMemo(
    () =>
      preparePoolMemberTableRows(
        translations,
        members.results,
        settings,
        renderUser,
        renderActions,
        renderStatus
      ),
    [
      translations,
      members.results,
      settings,
      renderUser,
      renderActions,
      renderStatus,
    ]
  );

  const changelogRows = useMemo(
    () =>
      prepareChangelogRows(
        changelog?.results,
        translations,
        renderUser,
        renderChangeLogEntryAction
      ),
    [changelog?.results, translations, renderUser, renderChangeLogEntryAction]
  );

  const memberActions = (
    <div className={classes.sectionActions}>
      <CustomSwitch
        className={classes.showRemovedSwitch}
        labelClass={classes.showRemovedLabel}
        label={translations.showRemoved}
        isChecked={shouldShowRemoved}
        onChange={handleShowRemovedChange}
      />
      {!isArchived ? (
        <CustomButton
          type="addDarkRoundedOutlined"
          onClick={handleOpenMemberDrawer()}
        >
          {translations.addMembers}
        </CustomButton>
      ) : null}
    </div>
  );

  return (
    <PageContainer
      {...rest}
      location={location}
      navigate={navigate}
      dispatch={dispatch}
      translations={translations}
      headerProps={{
        title: location?.state?.title || translations.title,
      }}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoading && pool && (
        <>
          <div className={classes.divider} />
          <SuccessionPoolInfo translations={translations} pool={pool} />
          <SuccessionPoolSection
            translations={translations}
            title={translations.sections.members}
            actions={memberActions}
            headers={memberHeaders}
            headerActions={memberActionsColumn}
            rows={memberRows}
            noResulltsMessage={translations.noMembers}
            hasLoadMore={hasNextMembersPage}
            onLoadMore={onLoadMoreMembers}
          />
          <SuccessionPoolSection
            translations={translations}
            title={translations.sections.changelog}
            headers={changelogHeaders}
            rows={changelogRows}
            noResulltsMessage={translations.noChangelog}
            hasLoadMore={hasNextChangelogPage}
            onLoadMore={onLoadMoreChangelog}
          />
          <CustomFormDrawer
            translations={translations.memberForm}
            titleText={memberDrawerTitle}
            isOpened={isManageDrawerOpened}
            isInitialValid={memberMode === EDIT}
            initialData={memberFormInitialData}
            fields={memberFormFields}
            employees={availableMembers}
            readinessOptions={readinessOptions}
            removalReasons={removalReasons}
            onClose={handleCloseMemberDrawer}
            onSave={handleSaveMember}
            shouldHideSave={isViewingRemoved}
            isMiddleWidth={!isViewingRemoved}
            hideDelete
            hasCancelButton
          />
        </>
      )}
    </PageContainer>
  );
};

SuccessionPoolPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SuccessionPoolPage);
